/**
 * Visualizer — real-time canvas waveform + vinyl disc spin control
 */

export class Visualizer {
  /**
   * @param {string} canvasId   — id of the <canvas> element
   * @param {AnalyserNode} analyser — from AudioEngine.analysers[id]
   * @param {'A'|'B'} deckId    — determines neon color
   */
  /**
   * @param {string} canvasId
   * @param {AnalyserNode} analyser
   * @param {'A'|'B'} deckId
   * @param {DeckController} [deck] — optional deck reference for timeline
   */
  constructor(canvasId, analyser, deckId, deck) {
    this.canvas   = document.getElementById(canvasId)
    this.ctx2d    = this.canvas?.getContext('2d')
    this.analyser = analyser
    this.deck     = deck || null
    this.deckId   = deckId
    this.color    = deckId === 'A' ? '#00ff88' : '#00aaff'
    this.glow     = deckId === 'A' ? 'rgba(0,255,136,0.5)' : 'rgba(0,170,255,0.5)'
    this._rafId   = null

    if (this.analyser) {
      this.analyser.fftSize = 2048
      this._bufLen  = this.analyser.frequencyBinCount
      this._timeDom = new Uint8Array(this._bufLen)
      this._freqDom = new Uint8Array(this._bufLen)
    }
  }

  startLoop() {
    if (!this.canvas || !this.ctx2d || !this.analyser) return
    const draw = () => {
      this._rafId = requestAnimationFrame(draw)
      this._drawWaveform()
    }
    draw()
  }

  stop() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  _drawWaveform() {
    const { canvas, ctx2d: ctx, analyser, color, glow } = this
    const W = canvas.width
    const H = canvas.height

    analyser.getByteTimeDomainData(this._timeDom)
    analyser.getByteFrequencyData(this._freqDom)

    // Background
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = 'rgba(10, 10, 15, 0.92)'
    ctx.fillRect(0, 0, W, H)

    // ── Frequency bars (background layer) ──
    const barCount  = 64
    const barWidth  = W / barCount
    const freqStep  = Math.floor(this._bufLen / barCount)

    ctx.globalAlpha = 0.3
    for (let i = 0; i < barCount; i++) {
      const val    = this._freqDom[i * freqStep] / 255
      const barH   = val * H * 0.8
      const x      = i * barWidth
      ctx.fillStyle = color
      ctx.fillRect(x, H - barH, barWidth - 1, barH)
    }
    ctx.globalAlpha = 1.0

    // ── Time-domain waveform (foreground) ──
    const grad = ctx.createLinearGradient(0, 0, W, 0)
    grad.addColorStop(0,   'transparent')
    grad.addColorStop(0.05, color)
    grad.addColorStop(0.95, color)
    grad.addColorStop(1,   'transparent')

    ctx.lineWidth      = 2.5
    ctx.strokeStyle    = grad
    ctx.shadowBlur     = 10
    ctx.shadowColor    = glow
    ctx.beginPath()

    const sliceW = W / this._bufLen
    let x = 0

    for (let i = 0; i < this._bufLen; i++) {
      const v = this._timeDom[i] / 128.0
      const y = (v * H) / 2
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
      x += sliceW
    }
    ctx.lineTo(W, H / 2)
    ctx.stroke()
    ctx.shadowBlur = 0

    // ── Centre playhead ──
    ctx.strokeStyle = 'rgba(255,255,255,0.25)'
    ctx.lineWidth   = 1
    ctx.beginPath()
    ctx.moveTo(W / 2, 0)
    ctx.lineTo(W / 2, H)
    ctx.stroke()

    // ── Timeline bar with markers ──
    this._drawTimeline(ctx, W, H)
  }

  /** Draw progress timeline at top of canvas with markers */
  _drawTimeline(ctx, W, H) {
    const deck = this.deck
    if (!deck || !deck.audioBuffer) return

    const duration = deck.duration
    if (duration <= 0) return

    const LANE_H = 6       // timeline lane height
    const LANE_Y = 2       // top margin
    const PAD    = 4       // left/right padding
    const usableW = W - PAD * 2

    // Helper: time → x position
    const tx = (t) => PAD + (t / duration) * usableW

    // ── Timeline track (dark background) ──
    ctx.fillStyle = 'rgba(255,255,255,0.06)'
    ctx.beginPath()
    ctx.roundRect(PAD, LANE_Y, usableW, LANE_H, 3)
    ctx.fill()

    // ── Loop region (shaded area) ──
    if (deck.loopIn !== null && deck.loopOut !== null) {
      const lx1 = tx(deck.loopIn)
      const lx2 = tx(deck.loopOut)
      ctx.fillStyle = deck.loopActive
        ? 'rgba(255,170,0,0.25)'
        : 'rgba(255,170,0,0.12)'
      ctx.fillRect(lx1, LANE_Y, lx2 - lx1, LANE_H)

      // Loop boundary markers
      ctx.fillStyle = '#ffaa00'
      ctx.fillRect(lx1, LANE_Y - 1, 1.5, LANE_H + 2)
      ctx.fillRect(lx2, LANE_Y - 1, 1.5, LANE_H + 2)
    }

    // ── Hot cue markers (small diamonds) ──
    const cueColors = ['#ff3366', '#ffaa00', '#00ccff', '#aa55ff']
    for (let i = 0; i < 4; i++) {
      if (deck.hotCues[i] !== null) {
        const cx = tx(deck.hotCues[i])
        ctx.fillStyle = cueColors[i]
        // Draw small triangle/arrow
        ctx.beginPath()
        ctx.moveTo(cx, LANE_Y - 1)
        ctx.lineTo(cx + 3, LANE_Y + LANE_H + 2)
        ctx.lineTo(cx - 3, LANE_Y + LANE_H + 2)
        ctx.closePath()
        ctx.fill()
      }
    }

    // ── Mix point marker (red glow line) ──
    if (deck.mixPoint !== null) {
      const mx = tx(deck.mixPoint)
      ctx.strokeStyle = '#ff2244'
      ctx.lineWidth = 1.5
      ctx.shadowBlur = 6
      ctx.shadowColor = 'rgba(255,34,68,0.8)'
      ctx.beginPath()
      ctx.moveTo(mx, LANE_Y - 2)
      ctx.lineTo(mx, LANE_Y + LANE_H + 3)
      ctx.stroke()
      ctx.shadowBlur = 0

      // Small "M" label
      ctx.fillStyle = '#ff2244'
      ctx.font = '6px monospace'
      ctx.fillText('M', mx - 2, LANE_Y + LANE_H + 10)
    }

    // ── Suggested mix point (yellow dashed) ──
    if (deck._suggestedMixPoint !== undefined && deck._suggestedMixPoint !== null) {
      const sx = tx(deck._suggestedMixPoint)
      ctx.strokeStyle = 'rgba(255,255,0,0.5)'
      ctx.lineWidth = 1
      ctx.setLineDash([2, 2])
      ctx.beginPath()
      ctx.moveTo(sx, LANE_Y - 1)
      ctx.lineTo(sx, LANE_Y + LANE_H + 2)
      ctx.stroke()
      ctx.setLineDash([])
    }

    // ── Playhead dot (glowing) ──
    const progress = deck.currentTime / duration
    const dotX = PAD + progress * usableW
    const dotY = LANE_Y + LANE_H / 2
    const dotR = 4

    // Glow halo
    ctx.shadowBlur = 12
    ctx.shadowColor = this.glow
    ctx.fillStyle = this.color
    ctx.beginPath()
    ctx.arc(dotX, dotY, dotR, 0, Math.PI * 2)
    ctx.fill()

    // Inner bright core
    ctx.shadowBlur = 0
    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    ctx.arc(dotX, dotY, 1.5, 0, Math.PI * 2)
    ctx.fill()

    // ── Elapsed line (subtle trail behind dot) ──
    ctx.strokeStyle = this.color
    ctx.globalAlpha = 0.3
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(PAD, dotY)
    ctx.lineTo(dotX, dotY)
    ctx.stroke()
    ctx.globalAlpha = 1.0
  }
}
